/**
 * push_copy.ts — 출하 푸시 알림 종류·문구(템플릿) 정의 (순수 도메인, firebase 의존 없음).
 *
 * 알림 본문은 동적 데이터를 치환변수 `{토큰}` 으로 담는 템플릿이다.
 *   예) "예정 {예정량} (관급 {관급} · 사급 {사급})"
 * 프론트(미리보기/테스트)와 백엔드(실발송)가 같은 renderTemplate 규칙을 쓴다.
 *   - 프론트: vars 에 각 변수의 "샘플값"을 넣어 미리보기/테스트.
 *   - 백엔드: vars 에 실제 출하 수치를 넣어 발송. (backend/functions/src 에 동일 구현)
 *
 * 저장: system_settings/push_notify_copy = { [type]: { title, body } }  (조직 공통, admin 편집)
 *       미설정/누락 키는 아래 default 로 폴백.
 *
 * ⚠️ 종류(키)를 추가하면 services/messaging(PUSH_NOTIFY_TYPES 재export),
 *    PushNotificationEditor 카드, backend 발송함수/PUSH_COPY_DEFAULTS 를 함께 갱신.
 */

export const PUSH_NOTIFY_TYPES = [
  // 출하(앱 푸시 기본)
  "morning_plan",
  "ship_start",
  "ship_finish",
  "ship_eod",
  // 관리(내부 벨 + 앱 푸시)
  "signup",
  "sales_sync",
  "rqc_drift",
] as const;
export type PushNotifyType = (typeof PUSH_NOTIFY_TYPES)[number];

/**
 * 알림 종류 → 앱 푸시 "수신 자격" 권한 키.
 * 이 권한을 가진 사용자(그룹·권한에서 부여)만 발송 대상이 된다. admin 은 전 권한 우회.
 * 빈 문자열("")이면 admin 전용(매핑 없음 → 백엔드가 admin 폴백).
 * 백엔드 발송 함수가 같은 매핑을 사용한다(backend/functions/src 에 동일 정의).
 * 도메인이 늘면 여기 + 백엔드 매핑을 함께 갱신.
 */
export const PUSH_TYPE_PERM: Record<PushNotifyType, string> = {
  morning_plan: "notify.shipment",
  ship_start: "notify.shipment",
  ship_finish: "notify.shipment",
  ship_eod: "notify.shipment",
  signup: "", // 관리자 전용 (가입 승인)
  sales_sync: "sales.view",
  rqc_drift: "shipment.view",
};

/** 종류별 본문에서 쓸 수 있는 치환변수 한 개 (토큰 = `{token}`). */
export interface PushCopyVar {
  /** 중괄호 안에 들어가는 토큰명 (예: "예정량") */
  token: string;
  /** 변수 설명 (칩 hover / 안내) */
  label: string;
  /** 미리보기·테스트용 샘플 표시값 */
  sample: string;
}

/** 알림 한 종류의 정의 (기본 문구 + 사용 가능 변수). */
export interface PushCopyDef {
  key: PushNotifyType;
  /** 카드 라벨 (PushNotificationEditor 와 공유) */
  name: string;
  defaultTitle: string;
  defaultBody: string;
  vars: PushCopyVar[];
}

/** 저장 문서의 한 항목 (편집 가능 문구). */
export interface PushCopyEntry {
  title: string;
  body: string;
}
/** system_settings/push_notify_copy 문서 형태 (부분 저장 가능). */
export type PushCopyMap = Partial<Record<PushNotifyType, Partial<PushCopyEntry>>>;

export const PUSH_COPY_DEFS: PushCopyDef[] = [
  {
    key: "morning_plan",
    name: "아침 출하 예정",
    defaultTitle: "🚚 오늘 출하 예정",
    defaultBody: "예정 {예정량} (관급 {관급} · 사급 {사급})",
    vars: [
      { token: "예정량", label: "오늘 출하 예정 물량", sample: "120㎥" },
      { token: "관급", label: "관급 예정 물량", sample: "80" },
      { token: "사급", label: "사급 예정 물량", sample: "40" },
    ],
  },
  {
    key: "ship_start",
    name: "금일 첫 출하 시작",
    defaultTitle: "🟢 금일 첫 출하 시작",
    defaultBody: "출하 시작 — {출하량} (관급 {관급} · 사급 {사급})",
    vars: [
      { token: "출하량", label: "현재까지 출하 물량", sample: "12.5㎥" },
      { token: "관급", label: "관급 출하 물량", sample: "7.5" },
      { token: "사급", label: "사급 출하 물량", sample: "5" },
    ],
  },
  {
    key: "ship_finish",
    name: "금일 출하 마무리",
    defaultTitle: "✅ 금일 출하 마무리",
    defaultBody: "총 {출하량} 출하 완료",
    vars: [{ token: "출하량", label: "오늘 총 출하 물량", sample: "250㎥" }],
  },
  {
    key: "ship_eod",
    name: "저녁 출하 마감 결산",
    defaultTitle: "📋 오늘 출하 마감",
    defaultBody: "{출하량}/{예정량} ({잔량})",
    vars: [
      { token: "출하량", label: "오늘 총 출하 물량", sample: "118.5㎥" },
      { token: "예정량", label: "오늘 출하 예정 물량", sample: "120㎥" },
      { token: "잔량", label: "남은 물량 (초과 시 '초과 N㎥')", sample: "잔량 1.5㎥" },
    ],
  },
  {
    key: "signup",
    name: "가입 승인 대기",
    defaultTitle: "👤 가입 승인 대기",
    defaultBody: "{대기건수}건이 승인을 기다립니다",
    vars: [{ token: "대기건수", label: "승인 대기 인원", sample: "2" }],
  },
  {
    key: "sales_sync",
    name: "영업 동기화 실패",
    defaultTitle: "⚠️ 영업 동기화 실패",
    defaultBody: "{시각} 동기화 실패 — 확인이 필요합니다",
    vars: [{ token: "시각", label: "실패 시각", sample: "16:00" }],
  },
  {
    key: "rqc_drift",
    name: "출하량 불일치 감지",
    defaultTitle: "🔁 출하량 불일치",
    defaultBody: "{월} 출하량 {차이} 차이 — 보정이 필요합니다",
    vars: [
      { token: "월", label: "대상 월", sample: "5월" },
      { token: "차이", label: "RQC와의 차이량", sample: "+12㎥" },
    ],
  },
];

const DEF_BY_KEY: Record<PushNotifyType, PushCopyDef> = Object.fromEntries(
  PUSH_COPY_DEFS.map((d) => [d.key, d]),
) as Record<PushNotifyType, PushCopyDef>;

export function pushCopyDef(key: PushNotifyType): PushCopyDef {
  return DEF_BY_KEY[key];
}

/**
 * `{토큰}` 을 vars 값으로 치환. vars 에 없는 토큰은 그대로 둠(편집 중 미리보기 안전).
 */
export function renderTemplate(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{([^{}]+)\}/g, (_m, k: string) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : `{${k}}`,
  );
}

/** 저장값(부분/누락) + 기본값을 합쳐 실제 사용할 {title, body} 도출. */
export function resolveCopy(map: PushCopyMap | undefined, key: PushNotifyType): PushCopyEntry {
  const def = DEF_BY_KEY[key];
  const saved = map?.[key];
  const title = saved?.title?.trim() ? saved.title : def.defaultTitle;
  const body = saved?.body?.trim() ? saved.body : def.defaultBody;
  return { title, body };
}

/** 종류별 미리보기/테스트용 샘플 변수 맵. */
export function sampleVars(key: PushNotifyType): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of DEF_BY_KEY[key].vars) out[v.token] = v.sample;
  return out;
}
